import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, FileText, Network, ListTree, Search as SearchIcon, BookOpenCheck, SlidersHorizontal } from 'lucide-react'
import mermaid from 'mermaid'
import Button from './ui/Button'
import { useProjects } from '../hooks/useRag'
import {
  useCodeRepos,
  useCodeOverview,
  useCodeModuleGraph,
  useCodeApiSurface,
  useCodeTrace,
  useCodeSymbol,
  useGenerateCodeDocs,
} from '../hooks/useResearch'
import { useSaga, SAGA_TERMINAL } from '../hooks/useImport'
import { BFF_ORIGIN } from '../services/config'

mermaid.initialize({ startOnLoad: false, securityLevel: 'loose', theme: 'neutral' })

// Renders Mermaid source client-side (bundled lib — no CDN, CSP-safe).
function MermaidDiagram({ code }) {
  const ref = useRef(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!code || !ref.current) return
    let alive = true
    const id = `mmd-${Math.random().toString(36).slice(2, 9)}`
    mermaid
      .render(id, code)
      .then(({ svg }) => {
        if (alive && ref.current) {
          ref.current.innerHTML = svg
          setErr(null)
        }
      })
      .catch((e) => alive && setErr(e.message))
    return () => {
      alive = false
    }
  }, [code])
  if (err) return <pre className="text-xs text-red-600 whitespace-pre-wrap">{err}</pre>
  return <div ref={ref} className="overflow-x-auto [&_svg]:max-w-none" />
}

const inputCls =
  'h-9 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

const TABS = [
  ['overview', 'Overview', ListTree],
  ['architecture', 'Architecture', Network],
  ['api', 'API', FileText],
  ['explore', 'Explore', SearchIcon],
]

// Doc chapters the user can pick before generating. `always` = part of the
// backbone the backend includes regardless (shown checked+locked). Order mirrors
// the report's table of contents.
const DOC_SECTIONS = [
  { id: 'inventory', label: 'Inventory', always: true },
  { id: 'techstack', label: 'Technology stack' },
  { id: 'config', label: 'Configuration' },
  { id: 'architecture', label: 'Architecture', always: true },
  { id: 'integrations', label: 'External dependencies' },
  { id: 'platform', label: 'Platform integration' },
  { id: 'api', label: 'API surface', always: true },
  { id: 'frontend', label: 'Frontend' },
  { id: 'datamodel', label: 'Data model' },
  { id: 'workflows', label: 'Workflows & jobs' },
  { id: 'flows', label: 'Flows' },
  { id: 'health', label: 'Code health' },
  { id: 'security', label: 'Security surface' },
  { id: 'dataflow', label: 'Data flow (egress)' },
  { id: 'runbook', label: 'Getting started' },
  { id: 'changelog', label: 'Changelog' },
  { id: 'narrative', label: 'AI narrative' },
]
const ALL_SECTION_IDS = DOC_SECTIONS.map((s) => s.id)

function TraceNode({ node, depth = 0 }) {
  return (
    <div style={{ marginLeft: depth ? 16 : 0 }} className="text-sm">
      <span className="font-mono text-ink dark:text-mist">{node.name}</span>
      <span className="ml-2 text-xs text-ink-soft dark:text-mist/50">{node.file}</span>
      {node.ref && <span className="ml-1 text-xs text-gold-deep dark:text-gold">↺</span>}
      {(node.calls ?? []).map((c, i) => (
        <TraceNode key={`${c.name}-${i}`} node={c} depth={depth + 1} />
      ))}
    </div>
  )
}

export default function CodeExplorer() {
  const projectsQ = useProjects()
  const projects = projectsQ.data?.projects ?? []
  const [project, setProject] = useState('')
  const codeReposQ = useCodeRepos(project)
  const repos = codeReposQ.data?.code_repos ?? []
  const [repo, setRepo] = useState('')
  const [tab, setTab] = useState('overview')

  // First project that actually has a bound repo makes a sensible default.
  useEffect(() => {
    if (!project && projects.length) setProject(projects[0].name)
  }, [projects, project])
  useEffect(() => {
    setRepo((r) => (repos.includes(r) ? r : repos[0] ?? ''))
  }, [repos])

  const overviewQ = useCodeOverview(repo, tab === 'overview')
  const graphQ = useCodeModuleGraph(repo, tab === 'architecture')
  const apiQ = useCodeApiSurface(repo, tab === 'api')

  // Explore: symbol lookup + trace
  const [symbol, setSymbol] = useState('')
  const [what, setWhat] = useState('definition')
  const lookup = useCodeSymbol()
  const trace = useCodeTrace()

  // One-click docs (saga → Report)
  const genDocs = useGenerateCodeDocs()
  const [docsSagaId, setDocsSagaId] = useState(null)
  // Chapter selection: which areas to generate (default all), plus the two
  // opt-in toggles. All selected → send no `sections` (= complete report).
  const [showChapters, setShowChapters] = useState(false)
  const [selectedSections, setSelectedSections] = useState(() => new Set(ALL_SECTION_IDS))
  const [dataDict, setDataDict] = useState(false)
  const [mkdocs, setMkdocs] = useState(true)
  const allSelected = selectedSections.size === ALL_SECTION_IDS.length
  const toggleSection = (id) =>
    setSelectedSections((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  const docsSaga = useSaga(docsSagaId)
  const docsSteps = Object.fromEntries((docsSaga.data?.steps ?? []).map((s) => [s.name, s]))
  const reportId = docsSteps.compose?.data?.report_id

  const apiGroups = useMemo(() => {
    const routes = apiQ.data?.http_routes ?? []
    const g = new Map()
    for (const r of routes) {
      const key = r.file
      if (!g.has(key)) g.set(key, [])
      g.get(key).push(r)
    }
    return [...g.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [apiQ.data])

  return (
    <div className="space-y-4">
      {/* repo picker + docs button */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={project} onChange={(e) => setProject(e.target.value)} className={inputCls} title="Project with bound code repos">
          {projects.map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        {repos.length > 0 ? (
          <select value={repo} onChange={(e) => setRepo(e.target.value)} className={`${inputCls} font-mono max-w-md`}>
            {repos.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        ) : (
          <input
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="C:\path\to\repo (no repos bound to this project)"
            className={`${inputCls} flex-1 min-w-64 font-mono`}
          />
        )}
        <button
          type="button"
          onClick={() => setShowChapters((v) => !v)}
          className={`${inputCls} inline-flex items-center gap-1 ${showChapters || !allSelected || dataDict ? 'ring-2 ring-gold/50' : ''}`}
          title="Choose which chapters to generate"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Chapters{allSelected ? '' : ` (${selectedSections.size})`}
        </button>
        <Button
          type="button"
          onClick={() =>
            genDocs.mutate(
              {
                project, repo_path: repo, mkdocs,
                sections: allSelected ? undefined : ALL_SECTION_IDS.filter((id) => selectedSections.has(id)),
                data_dictionary: dataDict,
              },
              { onSuccess: (r) => setDocsSagaId(r.saga_id) }
            )
          }
          loading={genDocs.isPending || (docsSagaId && !SAGA_TERMINAL.has(docsSaga.data?.status))}
          disabled={!repo}
          title="Compose a shareable documentation Report from the selected chapters"
        >
          <BookOpenCheck className="w-4 h-4 mr-1" />
          Generate docs
        </Button>
        {docsSagaId && docsSaga.data?.status === 'completed' && reportId && (
          <>
            <a
              className="text-sm text-gold-deep dark:text-gold underline underline-offset-2"
              href={`${BFF_ORIGIN}/api/reports/${reportId}`}
              target="_blank"
              rel="noreferrer"
            >
              Open report →
            </a>
            {docsSteps.compose?.data?.mkdocs_dir && (
              <span
                className="text-xs font-mono text-ink-soft dark:text-mist/60"
                title="Standalone MkDocs tree — run `mkdocs serve` in this directory for a searchable docs site"
              >
                mkdocs: {docsSteps.compose.data.mkdocs_dir}
              </span>
            )}
          </>
        )}
        {docsSagaId && (docsSaga.data?.status === 'failed' || docsSaga.data?.status === 'compensated') && (
          <span className="text-xs text-red-600">docs failed: {docsSaga.data?.error}</span>
        )}
        {genDocs.isError && <span className="text-xs text-red-600">{genDocs.error?.message}</span>}
      </div>

      {/* chapter selection */}
      {showChapters && (
        <div className="rounded-lg border border-parchment-border dark:border-midnight-600 p-3 bg-parchment/40 dark:bg-midnight-800/40 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-ink-soft dark:text-mist/70">Chapters to generate</span>
            <div className="flex gap-2 text-xs">
              <button type="button" className="text-gold-deep dark:text-gold underline underline-offset-2"
                onClick={() => setSelectedSections(new Set(ALL_SECTION_IDS))}>All</button>
              <button type="button" className="text-gold-deep dark:text-gold underline underline-offset-2"
                onClick={() => setSelectedSections(new Set(DOC_SECTIONS.filter((s) => s.always).map((s) => s.id)))}>Minimal</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
            {DOC_SECTIONS.map((s) => (
              <label key={s.id} className={`flex items-center gap-2 text-sm ${s.always ? 'opacity-60' : ''}`}
                title={s.always ? 'Always included — the report backbone' : ''}>
                <input
                  type="checkbox"
                  className="accent-gold"
                  checked={s.always || selectedSections.has(s.id)}
                  disabled={s.always}
                  onChange={() => toggleSection(s.id)}
                />
                <span className="text-ink dark:text-mist">{s.label}</span>
                {s.always && <span className="text-[10px] text-ink-soft dark:text-mist/50">(always)</span>}
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-parchment-border dark:border-midnight-600">
            <label className="flex items-center gap-2 text-sm" title="LLM pass: adds a plain-English purpose per entity and field meanings to the Data model chapter (costs tokens; needs a project)">
              <input type="checkbox" className="accent-gold" checked={dataDict} onChange={(e) => setDataDict(e.target.checked)} disabled={!project} />
              <span className="text-ink dark:text-mist">Data dictionary <span className="text-[10px] text-ink-soft dark:text-mist/50">(LLM · Data model)</span></span>
            </label>
            <label className="flex items-center gap-2 text-sm" title="Also export a standalone MkDocs site next to the report">
              <input type="checkbox" className="accent-gold" checked={mkdocs} onChange={(e) => setMkdocs(e.target.checked)} />
              <span className="text-ink dark:text-mist">MkDocs export</span>
            </label>
          </div>
        </div>
      )}

      {/* tabs */}
      <div className="inline-flex rounded-lg border border-parchment-border dark:border-midnight-600 p-0.5">
        {TABS.map(([t, label, Icon]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${
              tab === t ? 'bg-gold/15 text-gold-deep dark:text-gold' : 'text-ink-soft dark:text-mist/60'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {!repo && (
        <p className="text-sm text-ink-soft dark:text-mist/60">
          Bind a repo first: Sources → Add → Index a codebase (or pick a project that has one).
        </p>
      )}

      {/* Overview */}
      {tab === 'overview' && repo && (
        <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4">
          {overviewQ.isLoading && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
          {overviewQ.isError && <p className="text-sm text-red-600">{overviewQ.error?.message}</p>}
          {overviewQ.data && (
            <>
              <p className="text-sm text-ink-soft dark:text-mist/60 mb-3">
                {overviewQ.data.totals.files} files · {overviewQ.data.totals.modules} modules ·{' '}
                {overviewQ.data.totals.classes} classes · {overviewQ.data.totals.functions} functions
              </p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-ink-soft dark:text-mist/50">
                    <th className="py-1 pr-2 font-medium">Module</th>
                    <th className="py-1 pr-2 font-medium">Files</th>
                    <th className="py-1 pr-2 font-medium">Classes</th>
                    <th className="py-1 pr-2 font-medium">Functions</th>
                    <th className="py-1 pr-2 font-medium">Fan in/out</th>
                    <th className="py-1 font-medium">Top classes</th>
                  </tr>
                </thead>
                <tbody>
                  {overviewQ.data.modules.map((m) => (
                    <tr key={m.module} className="border-t border-parchment-border/40 dark:border-midnight-600/40 align-top">
                      <td className="py-1 pr-2 font-mono text-ink dark:text-mist">{m.module}</td>
                      <td className="py-1 pr-2">{m.files}</td>
                      <td className="py-1 pr-2">{m.classes}</td>
                      <td className="py-1 pr-2">{m.functions}</td>
                      <td className="py-1 pr-2">{m.fan_in}/{m.fan_out}</td>
                      <td className="py-1 text-ink-soft dark:text-mist/60">
                        {m.top_classes.map((c) => `${c.name} (${c.methods})`).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Architecture */}
      {tab === 'architecture' && repo && (
        <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4">
          {graphQ.isLoading && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
          {graphQ.isError && <p className="text-sm text-red-600">{graphQ.error?.message}</p>}
          {graphQ.data && (
            <>
              <p className="text-sm text-ink-soft dark:text-mist/60 mb-3">
                {graphQ.data.nodes} modules · {graphQ.data.edges} import edges
                {graphQ.data.truncated ? ' · truncated to the busiest modules' : ''}
              </p>
              <MermaidDiagram code={graphQ.data.diagram} />
            </>
          )}
        </div>
      )}

      {/* API */}
      {tab === 'api' && repo && (
        <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4 space-y-4">
          {apiQ.isLoading && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
          {apiQ.isError && <p className="text-sm text-red-600">{apiQ.error?.message}</p>}
          {apiQ.data && (
            <>
              <p className="text-sm text-ink-soft dark:text-mist/60">
                {apiQ.data.counts.http_routes} HTTP routes · {apiQ.data.counts.mcp_tools} MCP tools
              </p>
              {apiGroups.map(([file, routes]) => (
                <div key={file}>
                  <div className="text-xs font-mono text-gold-deep dark:text-gold mb-1">{file}</div>
                  {routes.map((r, i) => (
                    <div key={i} className="text-xs font-mono text-ink dark:text-mist">
                      <span className="inline-block w-14 text-ink-soft dark:text-mist/50">{r.method}</span>
                      {r.full_path || r.path}
                      <span className="ml-2 text-ink-soft/60 dark:text-mist/40">:{r.line}</span>
                    </div>
                  ))}
                </div>
              ))}
              {apiQ.data.mcp_tools.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-ink-soft dark:text-mist/50 mb-1">MCP tools</div>
                  <div className="flex flex-wrap gap-1.5">
                    {apiQ.data.mcp_tools.map((t, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-gold/10 text-gold-deep dark:text-gold text-xs font-mono" title={`${t.file}:${t.line}`}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Explore */}
      {tab === 'explore' && repo && (
        <div className="rounded-xl border border-parchment-border dark:border-midnight-600 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              placeholder="function / Class.method"
              className={`${inputCls} font-mono`}
              onKeyDown={(e) => e.key === 'Enter' && symbol.trim() && lookup.mutate({ repo_path: repo, symbol: symbol.trim(), what })}
            />
            <select value={what} onChange={(e) => setWhat(e.target.value)} className={inputCls}>
              {['definition', 'callers', 'callees', 'inheritance', 'examples'].map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
            <Button
              type="button"
              onClick={() => symbol.trim() && lookup.mutate({ repo_path: repo, symbol: symbol.trim(), what })}
              loading={lookup.isPending}
              disabled={!symbol.trim()}
            >
              Lookup
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => symbol.trim() && trace.mutate({ repo_path: repo, entry: symbol.trim(), max_depth: 3 })}
              loading={trace.isPending}
              disabled={!symbol.trim()}
              title="Walk the CALLS graph forward from this entrypoint (needs the code graph built)"
            >
              Trace flow
            </Button>
          </div>

          {lookup.isError && <p className="text-sm text-red-600">{lookup.error?.message}</p>}
          {lookup.data && (
            <div className="space-y-2">
              <p className="text-xs text-ink-soft dark:text-mist/50">
                {lookup.data.count} match(es){lookup.data.truncated ? ' (truncated)' : ''}
              </p>
              {lookup.data.results.map((r, i) => (
                <div key={i} className="text-sm border-t border-parchment-border/40 dark:border-midnight-600/40 pt-2">
                  <span className="font-mono text-ink dark:text-mist">
                    {r.method || r.caller || r.calls || r.class || r.test || '—'}
                  </span>
                  <span className="ml-2 text-xs text-ink-soft dark:text-mist/50">
                    {r.file}{r.line ? `:${r.line}` : ''}
                  </span>
                  {r.parameters && <div className="text-xs font-mono text-ink-soft dark:text-mist/60">({r.parameters})</div>}
                  {r.extends && <div className="text-xs text-ink-soft dark:text-mist/60">extends {r.extends}</div>}
                  {r.doc && <p className="mt-1 text-xs text-ink-soft dark:text-mist/70 italic">{r.doc}</p>}
                  {r.snippet && (
                    <pre className="mt-1 text-[11px] bg-parchment/60 dark:bg-midnight-800 rounded p-2 overflow-x-auto">{r.snippet}</pre>
                  )}
                </div>
              ))}
            </div>
          )}

          {trace.isError && <p className="text-sm text-red-600">{trace.error?.message}</p>}
          {trace.data && (
            <div className="border-t border-parchment-border/40 dark:border-midnight-600/40 pt-2">
              <p className="text-xs text-ink-soft dark:text-mist/50 mb-1">
                Call tree from <span className="font-mono">{trace.data.entry?.name}</span> — {trace.data.nodes} nodes,
                depth {trace.data.max_depth}{trace.data.truncated ? ' (truncated)' : ''} · ↺ = expanded elsewhere
              </p>
              <TraceNode node={trace.data.tree} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
